import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ENVIRONMENT } from '@ninety/mobile/platform/environments';

import { SKIP_SESSION } from './session-http.context';

/**
 * The Entitlements Token endpoint, and nothing else.
 *
 * Deliberately bare: no cache, no expiry check, no store, no retry. It has exactly one caller —
 * the Session Restore handler. The Entitlements Token carries the
 * Scopes that logically belong on the Access Token, so the two are one value, minted and discarded
 * together. A second caller would give it an independent lifetime — and a cached token on its own
 * refresh schedule can hold Scopes for a company the Access Token no longer belongs to.
 *
 * Scope *parsing* is not here either — `parseScopes()` is a pure function and Scopes are a selector
 * off the auth slice, so nothing stores them and nothing can disagree about them.
 */
@Injectable({ providedIn: 'root' })
export class EntitlementsApiService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT);

  /**
   * Fetch the Entitlements Token for a freshly minted Access Token.
   */
  fetch(
    /**
     * The Access Token is a parameter rather than something this reads: it exists nowhere the interceptor can see it,
     * because the machine is still `refreshing` and Resource Tokens only exist on `authorized`.
     */
    accessToken: string
  ): Observable<string> {
    return this.http.get<string>(`${this.environment.origin.entitlements}/v1/entitlements-token`, {
      headers: new HttpHeaders({ Authorization: `Bearer ${accessToken}` }),
      context: new HttpContext().set(SKIP_SESSION, true),
    });
  }
}
